// filepath: /home/toofoocustomer/tf-node/tf_customer_web/lib/customer/identity-api.ts
// 변경이력: 2026-11-02 R52. Social Desktop 개인·기관 가입 완료 전용 복귀 경로를 기존 서버 검증 OAuth resume에 한정한다.

export type CustomerSession = {
  authenticated: boolean;
  user_id?: string;
  session_id?: string;
  email?: string | null;
  authenticated_at?: string;
  expires_at?: string;
};

export type CustomerAccountSession = {
  session_kind: "COMMON_WEB" | "SOCIAL_PRIMARY" | "SOCIAL_COMPANION";
  current_session: boolean;
  can_revoke: boolean;
  management_token?: string | null;
  product_codes: string[];
  device_role?: "PRIMARY" | "COMPANION" | null;
  device_platform?: "ANDROID" | "IOS" | "DESKTOP" | null;
  device_class?: "PHONE" | "CELLULAR_TABLET" | "WIFI_TABLET" | "DESKTOP" | null;
  authenticated_at: string;
  last_seen_at: string;
  expires_at: string;
  liveness_expires_at?: string | null;
};

export type CustomerAccountSessionListResponse = {
  sessions: CustomerAccountSession[];
};

export type CustomerAccountReauthenticationResponse = {
  status: "reauthenticated";
  authenticated_at: string;
};

export type CustomerAccountDeletionResponse = {
  status: "deletion_started";
  identity_status: "DELETED";
  changed: boolean;
};

export type CustomerRegistrationResponse = {
  status: "registered";
  user_id: string;
  email: string;
  legal_name: string;
  birth_date: string;
  sex: "MALE" | "FEMALE";
  display_name: string;
  preferred_locale: string;
  home_region: string;
  phone_number: string;
  phone_verification_status: "PENDING";
  identity_verification_status: "UNVERIFIED";
};

export type CustomerRegistrationEmailCodeResponse = {
  status: "verification_code_requested";
  challenge_id: string | null;
  email: string;
  queued: boolean;
  resend_after_seconds: number;
};

export type CustomerRegistrationEmailVerifiedResponse = {
  status: "email_verified";
  challenge_id: string;
  email: string;
  verified_at: string;
};

export type CustomerAuthenticatedResponse = {
  status: "authenticated";
  user_id: string;
  email?: string;
};

export type AuthorizationPresentation = {
  brand_label: string;
  theme_mode: "LIGHT" | "DARK";
  page_background_color: string;
  surface_color: string;
  text_color: string;
  muted_text_color: string;
  accent_color: string;
  accent_text_color: string;
  border_color: string;
  input_background_color: string;
  focus_ring_color: string;
};

export type AuthorizationResumeContext = {
  client_id: string;
  client_name: string;
  product_code: string;
  presentation: AuthorizationPresentation;
};

export type CustomerPasswordResetAcceptedResponse = {
  status: "accepted";
};

export type CustomerPasswordResetResponse = {
  status: "password_reset";
};

const DEV_CUSTOMER_HOSTNAME = "testcustomer.toofoo.co.kr";
const DEV_IDENTITY_BASE_URL = "https://testidentity.toofoo.co.kr";
const LOCAL_IDENTITY_BASE_URL = "http://127.0.0.1:8100";

function configuredIdentityBaseUrl(): string | null {
  const configured = process.env.NEXT_PUBLIC_IDENTITY_BASE_URL?.trim();
  if (!configured) return null;

  let parsed: URL;
  try {
    parsed = new URL(configured);
  } catch {
    throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL must be an absolute URL.");
  }

  const hostname = parsed.hostname.toLowerCase();
  const loopback = hostname === "127.0.0.1"
    || hostname === "localhost"
    || hostname === "::1";

  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL must be an origin without credentials, query, or fragment.");
  }
  if (parsed.pathname !== "/") {
    throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL must not contain a path.");
  }
  if (parsed.protocol !== "https:" && !(parsed.protocol === "http:" && loopback)) {
    throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL must use HTTPS except for loopback development.");
  }

  return parsed.origin;
}

export function isDevelopmentCustomerHost(): boolean {
  if (typeof window === "undefined") return false;

  const hostname = window.location.hostname.toLowerCase();
  return hostname === DEV_CUSTOMER_HOSTNAME
    || hostname === "127.0.0.1"
    || hostname === "localhost"
    || hostname === "::1";
}

function identityBaseUrl(): string {
  const configured = configuredIdentityBaseUrl();

  if (typeof window === "undefined") {
    if (!configured) {
      throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL is required for server-side Identity access.");
    }
    return configured;
  }

  const hostname = window.location.hostname.toLowerCase();
  const loopback = hostname === "127.0.0.1"
    || hostname === "localhost"
    || hostname === "::1";

  if (hostname === DEV_CUSTOMER_HOSTNAME) {
    if (configured && configured !== DEV_IDENTITY_BASE_URL) {
      throw new Error("DEV Customer must use the DEV Common Identity origin.");
    }
    return DEV_IDENTITY_BASE_URL;
  }

  if (loopback) {
    if (configured && configured !== DEV_IDENTITY_BASE_URL && configured !== LOCAL_IDENTITY_BASE_URL) {
      throw new Error("Local Customer development cannot use a non-DEV Identity origin.");
    }
    return configured ?? LOCAL_IDENTITY_BASE_URL;
  }

  if (!configured) {
    throw new Error("NEXT_PUBLIC_IDENTITY_BASE_URL is required outside DEV Customer hosts.");
  }

  return configured;
}

export function customerAuthorizationResumeUrl(resume: string): string {
  const query = new URLSearchParams({ resume });
  return `${identityBaseUrl()}/oauth/resume?${query.toString()}`;
}

async function parsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return null;
  return response.json().catch(() => null);
}

export class CustomerIdentityApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(status: number, payload: unknown, fallbackMessage: string) {
    super(fallbackMessage);
    this.name = "CustomerIdentityApiError";
    this.status = status;
    this.payload = payload;
  }
}

async function identityFetch<T>(
  pathname: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${identityBaseUrl()}${pathname}`, {
    ...init,
    cache: "no-store",
    credentials: "include",
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });

  const payload = await parsePayload(response);
  if (!response.ok) {
    throw new CustomerIdentityApiError(
      response.status,
      payload,
      "요청을 처리하지 못했습니다.",
    );
  }

  return payload as T;
}

export async function getAuthorizationResumeContext(
  resume: string,
): Promise<AuthorizationResumeContext> {
  const query = new URLSearchParams({ resume });
  return identityFetch<AuthorizationResumeContext>(
    `/oauth/resume-context?${query.toString()}`,
    { method: "GET" },
  );
}

export async function requestRegistrationEmailCode(input: {
  email: string;
  preferred_locale: string;
  challenge_id?: string;
}): Promise<CustomerRegistrationEmailCodeResponse> {
  return identityFetch<CustomerRegistrationEmailCodeResponse>(
    "/v1/auth/registration/email-code",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function verifyRegistrationEmailCode(input: {
  challenge_id: string;
  email: string;
  verification_code: string;
}): Promise<CustomerRegistrationEmailVerifiedResponse> {
  return identityFetch<CustomerRegistrationEmailVerifiedResponse>(
    "/v1/auth/registration/email-code/verify",
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
}

export async function registerCustomer(input: {
  email_verification_challenge_id?: string | null;
  resume?: string | null;
  legal_name: string;
  birth_date: string;
  sex: "MALE" | "FEMALE";
  display_name: string;
  preferred_locale: string;
  home_region: string;
  phone_number: string;
  phone_region_code?: string | null;
  email: string;
  password: string;
  terms_accepted: boolean;
  privacy_notice_acknowledged: boolean;
  marketing_consent: boolean;
}): Promise<CustomerRegistrationResponse> {
  return identityFetch<CustomerRegistrationResponse>("/v1/auth/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function resendCustomerVerificationEmail(
  email: string,
): Promise<{ status: "accepted" }> {
  return identityFetch<{ status: "accepted" }>("/v1/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function verifyCustomerEmailCode(
  email: string,
  verificationCode: string,
  resume?: string,
): Promise<CustomerAuthenticatedResponse> {
  return identityFetch<CustomerAuthenticatedResponse>("/v1/auth/verify-email-code", {
    method: "POST",
    body: JSON.stringify({
      email,
      verification_code: verificationCode,
      ...(resume ? { resume } : {}),
    }),
  });
}

export async function requestCustomerPasswordReset(
  email: string,
): Promise<CustomerPasswordResetAcceptedResponse> {
  return identityFetch<CustomerPasswordResetAcceptedResponse>("/v1/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function resetCustomerPassword(
  resetToken: string,
  newPassword: string,
): Promise<CustomerPasswordResetResponse> {
  return identityFetch<CustomerPasswordResetResponse>("/v1/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({
      reset_token: resetToken,
      new_password: newPassword,
    }),
  });
}

export async function loginCustomer(
  email: string,
  password: string,
  resume?: string,
): Promise<CustomerAuthenticatedResponse> {
  return identityFetch<CustomerAuthenticatedResponse>("/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      ...(resume ? { resume } : {}),
    }),
  });
}

export async function getCustomerSession(): Promise<CustomerSession> {
  return identityFetch<CustomerSession>("/v1/auth/session", {
    method: "GET",
  });
}

export async function logoutCustomer(): Promise<void> {
  await identityFetch<null>("/v1/auth/logout", {
    method: "POST",
  });
}

export async function getCustomerAccountSessions(): Promise<CustomerAccountSessionListResponse> {
  return identityFetch<CustomerAccountSessionListResponse>("/v1/account/sessions", {
    method: "GET",
  });
}

export async function revokeCustomerAccountSession(managementToken: string): Promise<{
  status: "session_revoked";
  changed: boolean;
}> {
  return identityFetch<{ status: "session_revoked"; changed: boolean }>(
    "/v1/account/sessions/revoke",
    {
      method: "POST",
      body: JSON.stringify({ management_token: managementToken }),
    },
  );
}

export async function reauthenticateCustomerAccount(
  password: string,
): Promise<CustomerAccountReauthenticationResponse> {
  return identityFetch<CustomerAccountReauthenticationResponse>(
    "/v1/account/reauthenticate",
    {
      method: "POST",
      body: JSON.stringify({ password }),
    },
  );
}

export async function deleteCustomerAccount(): Promise<CustomerAccountDeletionResponse> {
  return identityFetch<CustomerAccountDeletionResponse>("/v1/account/delete", {
    method: "POST",
    body: JSON.stringify({ confirmation: "DELETE_TOOFOO_ACCOUNT" }),
  });
}

function detailCode(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null;
  const detail = (payload as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === "string" ? code : null;
}

export function customerIdentityErrorMessage(
  error: unknown,
  context: "register" | "registration-email" | "resend" | "login" | "verify" | "forgot" | "reset" | "session" | "logout" | "profile" | "account-security" | "account-reauth" | "account-delete",
): string {
  if (!(error instanceof CustomerIdentityApiError)) {
    return "서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.";
  }

  const code = detailCode(error.payload);

  if (context === "registration-email") {
    if (code === "customer_email_already_registered") {
      return "이미 가입된 이메일입니다. 이메일 인증부터 다시 확인해 주세요.";
    }
    if (code === "customer_registration_email_verification_required") {
      return "이메일 인증을 먼저 완료해 주세요.";
    }
    if (code === "customer_registration_email_challenge_invalid") {
      return "이메일 인증 요청이 만료되었습니다. 인증번호 받기부터 다시 진행해 주세요.";
    }
    if (code === "customer_registration_email_code_not_ready") {
      return "인증코드 발송 처리 중입니다. 메일을 받은 뒤 다시 확인해 주세요.";
    }
    if (code === "customer_email_verification_code_invalid") {
      return "인증코드가 올바르지 않거나 만료되었습니다. 새 코드를 받아 다시 확인해 주세요.";
    }
    if (error.status === 422) {
      return "이메일 주소를 확인해 주세요.";
    }
  }

  if (context === "register") {
    if (code === "authorization_resume_invalid" || code === "authorization_resume_binding_failed") {
      return "서비스 가입 요청이 만료되었습니다. 이용하려던 서비스에서 다시 시작해 주세요.";
    }
    if (code === "customer_email_already_registered") {
      return "이미 가입된 이메일입니다. 로그인해 주세요.";
    }
    if (error.status === 422) {
      return "이름, 국가/지역, 휴대폰 번호, 이메일과 비밀번호를 확인해 주세요.";
    }
  }

  if (context === "resend") {
    return "인증코드를 다시 보내지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  if (context === "login") {
    if (code === "authorization_resume_invalid" || code === "authorization_resume_binding_failed") {
      return "서비스 로그인 요청이 만료되었습니다. 이용하려던 서비스에서 다시 로그인해 주세요.";
    }
    if (error.status === 401) {
      return "이메일 또는 비밀번호가 올바르지 않거나 이메일 확인이 필요합니다.";
    }
  }

  if (context === "verify" && error.status === 400) {
    return "인증코드가 올바르지 않거나 만료되었습니다. 코드를 확인하거나 새 코드를 받아 주세요.";
  }

  if (context === "forgot") {
    return "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  if (context === "reset" && error.status === 400) {
    return "비밀번호 재설정 링크가 유효하지 않거나 만료되었습니다.";
  }

  if (context === "reset" && error.status === 422) {
    return "새 비밀번호는 8자 이상 입력해 주세요.";
  }

  if (context === "session" && error.status === 401) {
    return "로그인이 필요합니다.";
  }


  if (context === "profile" && error.status === 422) {
    return "입력한 기본 정보를 확인해 주세요.";
  }

  if (context === "account-security") {
    if (code === "ACCOUNT_SESSION_REQUIRED") {
      return "로그인이 필요합니다.";
    }
    if (code === "CURRENT_SESSION_USE_LOGOUT") {
      return "현재 브라우저는 계정 화면의 로그아웃을 사용해 주세요.";
    }
    if (code === "ACCOUNT_SESSION_MANAGEMENT_TOKEN_INVALID") {
      return "기기 목록이 변경되었습니다. 목록을 새로 확인해 주세요.";
    }
    return "기기와 세션 정보를 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  if (context === "account-reauth") {
    if (code === "ACCOUNT_REAUTHENTICATION_FAILED") {
      return "비밀번호가 올바르지 않습니다.";
    }
    if (code === "ACCOUNT_SESSION_REQUIRED") {
      return "로그인이 만료되었습니다. 다시 로그인해 주세요.";
    }
    return "본인 확인을 완료하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  if (context === "account-delete") {
    if (code === "FRESH_AUTHENTICATION_REQUIRED") {
      return "계정 삭제 전에 비밀번호로 본인 확인을 다시 해 주세요.";
    }
    if (code === "ACCOUNT_SESSION_REQUIRED") {
      return "로그인이 만료되었습니다. 다시 로그인해 주세요.";
    }
    return "계정 삭제 요청을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.";
  }

  return "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

export type CustomerContactPoint = {
  value: string;
  primary: boolean;
  verification_status: "PENDING" | "VERIFIED" | "REVOKED";
  verified: boolean;
  verified_at?: string | null;
  verification_method?: string | null;
  region_code?: string | null;
};

export type CustomerIdentityVerification = {
  status: "UNVERIFIED" | "VERIFIED";
  verified: boolean;
  provider?: string | null;
  verified_at?: string | null;
};

export type CustomerCoreProfile = {
  user_id: string;
  display_name?: string | null;
  legal_name?: string | null;
  birth_date?: string | null;
  sex?: "MALE" | "FEMALE" | null;
  identity_verification?: CustomerIdentityVerification;
  preferred_locale?: string | null;
  home_region?: string | null;
  data_region?: string | null;
  email?: CustomerContactPoint | null;
  phone?: CustomerContactPoint | null;
};

export type CustomerProfileRequirement = {
  attribute_code: "DISPLAY_NAME" | "LEGAL_NAME" | "BIRTH_DATE" | "SEX" | "LOCALE" | "EMAIL" | "PHONE";
  requirement_level: "PRESENT" | "VERIFIED";
  satisfied: boolean;
};

export type CustomerProfileCompletion = {
  client_id: string;
  client_name: string;
  product_code: string;
  profile_complete: boolean;
  requirements: CustomerProfileRequirement[];
  profile: CustomerCoreProfile;
};

function profilePath(pathname: string, resume?: string): string {
  if (!resume) return pathname;
  const query = new URLSearchParams({ resume });
  return `${pathname}?${query.toString()}`;
}

export async function getCustomerCoreProfile(resume?: string): Promise<CustomerCoreProfile> {
  return identityFetch<CustomerCoreProfile>(
    profilePath("/v1/profile/me", resume),
    { method: "GET" },
  );
}

export async function updateCustomerCoreProfile(input: {
  display_name?: string | null;
  legal_name?: string | null;
  birth_date?: string | null;
  sex?: "MALE" | "FEMALE" | null;
  preferred_locale?: string | null;
  home_region?: string | null;
},
  resume?: string,
): Promise<CustomerCoreProfile> {
  return identityFetch<CustomerCoreProfile>(
    profilePath("/v1/profile/me", resume),
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
}

export async function updateCustomerPrimaryPhone(
  input: {
    phone_number: string;
    region_code?: string | null;
  },
  resume?: string,
): Promise<CustomerCoreProfile> {
  return identityFetch<CustomerCoreProfile>(
    profilePath("/v1/profile/me/phone", resume),
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
}

export async function getCustomerProfileCompletion(
  resume: string,
): Promise<CustomerProfileCompletion> {
  const query = new URLSearchParams({ resume });
  return identityFetch<CustomerProfileCompletion>(
    `/v1/profile/completion?${query.toString()}`,
    { method: "GET" },
  );
}


const SOCIAL_DESKTOP_OAUTH_CLIENT_ID = "social-desktop-dev";

export async function customerAuthorizationAfterSignupUrl(resume: string): Promise<string> {
  const original = customerAuthorizationResumeUrl(resume);
  try {
    const context = await getAuthorizationResumeContext(resume);
    if (context.product_code === "SOCIAL" && context.client_id === SOCIAL_DESKTOP_OAUTH_CLIENT_ID) {
      return `/social/desktop/complete?${new URLSearchParams({ resume }).toString()}`;
    }
  } catch {
    // Existing OAuth resume remains authoritative if the optional UI lookup fails.
  }
  return original;
}

export function isSocialDesktopResume(context: AuthorizationResumeContext): boolean {
  return context.product_code === "SOCIAL" && context.client_id === SOCIAL_DESKTOP_OAUTH_CLIENT_ID;
}
